import { DialogTrigger, Input } from "@light-technology/components";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "../../state/api";
import {
  BrandMode,
  type AppConfigurationResponse,
  type AppResponse,
} from "../../state/api.types";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { BasicModal } from "../shared/BasicModal";
import { FormCard } from "../shared/FormCard";

type AppearanceNameForm = {
  company_name: string;
};

type AppearanceNameCardProps = {
  app: AppResponse;
  configuration: AppConfigurationResponse;
};

export const AppearanceNameCard = (props: AppearanceNameCardProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingCompanyName, setPendingCompanyName] = useState<string | null>(
    null,
  );

  const [updateConfiguration, updateConfigurationResult] = useMutationWithToast(
    api.useUpdateConfigurationMutation(),
    { successMessage: "The app's brand name has been updated" },
  );

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isValid },
  } = useForm<AppearanceNameForm>({
    defaultValues: { company_name: props.configuration.company_name },
    mode: "onChange",
  });

  const openConfirmModal = handleSubmit(({ company_name }) => {
    setPendingCompanyName(company_name);
    setIsConfirmOpen(true);
  });

  const onConfirmSave = () => {
    if (pendingCompanyName === null) return;
    updateConfiguration({
      appUUID: props.app.uuid,
      company_name: pendingCompanyName,
    })
      .unwrap()
      .then(() => {
        reset({ company_name: pendingCompanyName });
        setIsConfirmOpen(false);
        setPendingCompanyName(null);
      })
      .catch(() => {
        // Error surfaced by useMutationWithToast; keep the modal open for retry.
      });
  };

  const onCancelConfirm = () => {
    setIsConfirmOpen(false);
    setPendingCompanyName(null);
  };

  return (
    <>
      <FormCard
        name="Brand name"
        subtext={
          <>
            This name is shared with your customers and prefixed to any plan
            name within the app. Go to{" "}
            <Link className="underline" to="/settings/team">
              Settings
            </Link>{" "}
            to change organization name for your teammates.
          </>
        }
        buttonText="Save name"
        isDisabled={!isDirty || !isValid}
        isLoading={updateConfigurationResult.isLoading}
        onButtonClick={openConfirmModal}
        data-ph-capture-attribute-action="update-brand-name"
      >
        <Controller
          control={control}
          name="company_name"
          rules={{ required: "Brand name is required" }}
          render={({ field, fieldState }) => (
            <>
              <Input
                {...field}
                type="text"
                isRequired
                aria-label="Brand name"
                validationBehavior="aria"
                isInvalid={fieldState.invalid}
                hint={fieldState.error?.message}
              />
              <div className="mt-3xl space-y-sm rounded-xl bg-tertiary p-xl">
                <div className="text-sm font-medium text-secondary">
                  Customer previews
                </div>
                {props.configuration.brand_mode !== BrandMode.Subtle && (
                  <div className="text-sm text-secondary">
                    "Thanks for signing up for the{" "}
                    {field.value ? field.value : "[brand name]"} Big Time
                    Buyback Plan."
                  </div>
                )}
                <div className="text-sm text-secondary">
                  "{field.value ? field.value : "[brand name]"} electricity
                  plans are available at your location."
                </div>
                <div className="text-sm text-secondary">
                  "{field.value ? field.value : "[brand name]"} works with Light
                  to provide your energy plans."
                </div>
              </div>
            </>
          )}
        />
      </FormCard>

      <DialogTrigger
        isOpen={isConfirmOpen}
        onOpenChange={(open: boolean) => {
          setIsConfirmOpen(open);
          if (!open) setPendingCompanyName(null);
        }}
      >
        <BasicModal
          textTitle={`Do you own the ${pendingCompanyName ?? "this"} brand?`}
          textDescription="We only allow brand names that the partner owns to prevent confusing experiences for customers."
          textCancel="Cancel"
          textSubmit="Yes, save"
          isDisabled={updateConfigurationResult.isLoading}
          isLoading={updateConfigurationResult.isLoading}
          onCancel={onCancelConfirm}
          onSubmit={onConfirmSave}
          data-ph-capture-attribute-action="confirm-save-brand-name"
        />
      </DialogTrigger>
    </>
  );
};
